import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Download, X, Share, Plus, MoreVertical } from 'lucide-react';

const InstallAppPrompt = () => {
  const { language, localize } = useLanguage();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  const isAr = language === 'ar';

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
      return;
    }

    // Check if dismissed recently (don't show for 3 days)
    const dismissed = localStorage.getItem('pwa_dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 3 * 24 * 60 * 60 * 1000) return;

    // Detect iOS
    const ua = window.navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(isiOS);

    if (isiOS) {
      // On iOS, show after 2 seconds
      const timer = setTimeout(() => setShowPrompt(true), 2000);
      return () => clearTimeout(timer);
    }

    // Android/Desktop: listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), 1500);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    });

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = useCallback(async () => {
    if (isIOS) {
      setShowIOSGuide(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  }, [isIOS, deferredPrompt]);

  const handleDismiss = useCallback(() => {
    setShowPrompt(false);
    setShowIOSGuide(false);
    localStorage.setItem('pwa_dismissed', Date.now().toString());
  }, []);

  if (isInstalled || !showPrompt) return null;

  // iOS instruction modal
  if (showIOSGuide) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-end justify-center bg-black/50 backdrop-blur-sm" onClick={handleDismiss}>
        <div
          className="w-full max-w-md bg-white rounded-t-3xl p-6 pb-10 animate-slide-up"
          onClick={(e) => e.stopPropagation()}
          dir={isAr ? 'rtl' : 'ltr'}
          data-testid="ios-install-guide"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-slate-900">
              {localize({ ar: 'تثبيت التطبيق', en: 'Install App' })}
            </h3>
            <button
              onClick={handleDismiss}
              className="p-2 rounded-full hover:bg-slate-100 transition-colors"
              data-testid="ios-guide-close"
            >
              <X size={20} className="text-slate-500" />
            </button>
          </div>

          <div className="flex items-center gap-4 mb-6">
            <img src="/icon-192.png" alt="Learn With Trust" className="w-16 h-16 rounded-2xl shadow-md" />
            <div>
              <p className="font-semibold text-slate-900">Learn With Trust</p>
              <p className="text-sm text-slate-500">
                {localize({ ar: 'منصة تعلم مع ترست', en: 'Learn With Trust Platform' })}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-start gap-4 p-3 bg-emerald-50 rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <span className="text-lg font-bold text-emerald-600">1</span>
              </div>
              <div className="flex-1">
                <p className="font-medium text-slate-800">
                  {localize({ ar: 'اضغط على زر المشاركة', en: 'Tap the Share button' })}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <Share size={18} className="text-emerald-500" />
                  <p className="text-sm text-slate-500">
                    {localize({ ar: 'الزر في أسفل المتصفح (Safari)', en: 'At the bottom of Safari browser' })}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-4 p-3 bg-emerald-50 rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <span className="text-lg font-bold text-emerald-600">2</span>
              </div>
              <div className="flex-1">
                <p className="font-medium text-slate-800">
                  {localize({ ar: 'اختر "إضافة إلى الشاشة الرئيسية"', en: 'Select "Add to Home Screen"' })}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <Plus size={18} className="text-slate-600" />
                  <p className="text-sm text-slate-500">
                    {localize({ ar: 'مرر للأسفل في القائمة', en: 'Scroll down in the menu' })}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-4 p-3 bg-emerald-50 rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <span className="text-lg font-bold text-emerald-600">3</span>
              </div>
              <div className="flex-1">
                <p className="font-medium text-slate-800">
                  {localize({ ar: 'اضغط "إضافة"', en: 'Tap "Add"' })}
                </p>
                <p className="text-sm text-slate-500">
                  {localize({ ar: 'سيظهر التطبيق على شاشتك الرئيسية', en: 'The app will appear on your home screen' })}
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={handleDismiss}
            className="w-full mt-6 py-3 bg-slate-100 text-slate-700 rounded-2xl font-medium hover:bg-slate-200 transition-colors"
            data-testid="ios-guide-got-it"
          >
            {localize({ ar: 'فهمت!', en: 'Got it!' })}
          </button>
        </div>
      </div>
    );
  }

  // Main install banner
  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-[9998] max-w-md mx-auto animate-slide-up"
      dir={isAr ? 'rtl' : 'ltr'}
      data-testid="install-app-banner"
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-emerald-100 overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 p-0.5" />
        <div className="p-4">
          <div className="flex items-center gap-3">
            <img src="/icon-192.png" alt="LWT" className="w-12 h-12 rounded-xl shadow" />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-slate-900 text-sm">
                {localize({ ar: 'ثبّت التطبيق على جهازك', en: 'Install app on your device' })}
              </p>
              <p className="text-xs text-slate-500 truncate">
                {localize({ ar: 'وصول سريع بدون متصفح', en: 'Quick access without a browser' })}
              </p>
            </div>
            <button
              onClick={handleDismiss}
              className="p-1.5 rounded-full hover:bg-slate-100 transition-colors flex-shrink-0"
              data-testid="install-banner-dismiss"
            >
              <X size={16} className="text-slate-400" />
            </button>
          </div>

          <div className="flex gap-2 mt-3">
            <button
              onClick={handleInstall}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium text-sm transition-colors active:scale-[0.98]"
              data-testid="install-app-button"
            >
              <Download size={16} />
              {localize({ ar: 'تثبيت الآن', en: 'Install Now' })}
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-2.5 text-slate-500 hover:bg-slate-50 rounded-xl text-sm transition-colors"
              data-testid="install-later-button"
            >
              {localize({ ar: 'لاحقاً', en: 'Later' })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallAppPrompt;
